// src/hooks/useTypedTranslation.ts
import { useTranslation } from "react-i18next";

// Namespace type
export type Namespace =
  | "common"
  | "auth"
  | "home"
  | "recipes"
  | "preferences"
  | "planner"
  | "shopping"
  | "orders"
  | "errors"
  | "admin"
  | "profile"
  | "legal";

// Enhanced hook with proper typing
export function useTypedTranslation(ns?: Namespace | Namespace[]) {
  const { t, i18n, ready } = useTranslation(ns);

  return {
    t,
    i18n,
    ready,
    /**
     * Current language code
     */
    language: i18n.language,
    /**
     * Change language
     */
    changeLanguage: (lng: string) => i18n.changeLanguage(lng),
  };
}

/**
 * Hook for translations with specific namespace
 * Provides type-safe access to translation keys within a namespace
 */
export function useNamespacedTranslation<NS extends Namespace>(namespace: NS) {
  const { t, i18n, ready } = useTranslation(namespace);

  return {
    t: (key: string, options?: Record<string, unknown>) => t(key, options),
    i18n,
    ready,
  };
}

/**
 * Hook for common translations (most frequently used)
 */
export function useCommonTranslation() {
  return useTypedTranslation("common");
}

/**
 * Hook for authentication translations
 */
export function useAuthTranslation() {
  return useTypedTranslation("auth");
}

/**
 * Hook for home page translations
 */
export function useHomeTranslation() {
  return useTypedTranslation("home");
}

/**
 * Hook for recipe translations
 */
export function useRecipeTranslation() {
  return useTypedTranslation("recipes");
}

/**
 * Hook for preferences translations
 */
export function usePreferencesTranslation() {
  return useTypedTranslation("preferences");
}

/**
 * Hook for planner translations
 */
export function usePlannerTranslation() {
  return useTypedTranslation("planner");
}

/**
 * Hook for shopping list translations
 */
export function useShoppingTranslation() {
  return useTypedTranslation("shopping");
}

/**
 * Hook for orders translations
 */
export function useOrdersTranslation() {
  return useTypedTranslation("orders");
}

/**
 * Hook for error translations
 */
export function useErrorTranslation() {
  return useTypedTranslation("errors");
}

/**
 * Hook for admin translations
 */
export function useAdminTranslation() {
  return useTypedTranslation("admin");
}

/**
 * Hook for profile translations
 */
export function useProfileTranslation() {
  return useTypedTranslation("profile");
}

/**
 * Hook for legal and consent translations
 */
export function useLegalTranslation() {
  return useTypedTranslation("legal");
}
